// OWNER: Agent A — do not edit if you are not Agent A.
// AuthProvider / useAuth() / <ProtectedRoute>. This is the one shared boundary
// with Agent C: wrap the app root in <AuthProvider> and gate protected routes
// with <ProtectedRoute>. api.ts already hard-redirects to /login on any 401,
// so ProtectedRoute's own redirect mostly covers the adminOnly case and the
// brief window before the initial /auth/me call resolves.
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import type { User } from "@shared/types";
import { get, post } from "./api";

export type AuthStatus = "loading" | "authenticated" | "anonymous";

export interface AuthContextValue {
  user: User | null;
  status: AuthStatus;
  /** POST /api/auth/request-otp. Resolves to the generic "OTP sent" message. */
  requestOtp: (email: string) => Promise<{ message: string }>;
  /** POST /api/auth/verify-otp. Resolves to the signed-in user and updates auth state. */
  verifyOtp: (email: string, code: string) => Promise<User>;
  logout: () => Promise<void>;
  /** Re-checks the session against GET /api/auth/me. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const refresh = useCallback(async () => {
    try {
      const { user } = await get<{ user: User }>("/auth/me");
      setUser(user);
      setStatus("authenticated");
    } catch {
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const requestOtp = useCallback(
    (email: string) => post<{ message: string }>("/auth/request-otp", { email }),
    [],
  );

  const verifyOtp = useCallback(async (email: string, code: string) => {
    const { user } = await post<{ user: User }>("/auth/verify-otp", { email, code });
    setUser(user);
    setStatus("authenticated");
    return user;
  }, []);

  const logout = useCallback(async () => {
    await post("/auth/logout");
    setUser(null);
    setStatus("anonymous");
  }, []);

  return (
    <AuthContext.Provider value={{ user, status, requestOtp, verifyOtp, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export function ProtectedRoute({
  children,
  adminOnly = false,
}: {
  children: ReactNode;
  adminOnly?: boolean;
}) {
  const { user, status } = useAuth();
  const location = useLocation();

  if (status === "loading") return null;

  if (status === "anonymous" || !user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
